import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiEdit, FiTrash2, FiMail, FiUser, FiUserCheck, FiUserX } from 'react-icons/fi';
import { customerAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';
import SuccessAlert from '../../components/SuccessAlert';

const UserManagement = () => {
  const { user: currentUser, loading: authLoading } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: ''
    // role is always 'regular_user' for new invites
  });
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    permissions: [],
    isActive: true
  });

  const queryClient = useQueryClient();

  // Check if current user can invite users
  const canInviteUsers = () => {
    // Don't show errors if still loading
    if (authLoading || !currentUser) {
      return false;
    }

    // Customer admin can always invite
    if (currentUser?.role === 'customer_admin' || currentUser?.is_primary) {
      return true;
    }

    // Also check if roles array contains customer_admin
    if (currentUser?.roles && currentUser.roles.includes('customer_admin')) {
      return true;
    }

    return false;
  };

  // Fetch company users
  const { data: users, isLoading, error, refetch } = useQuery(
    'company-users',
    customerAPI.getUsers,
    {
      staleTime: 30000,
      onError: (error) => {
        console.error('Error fetching users:', error);
        setErrorMessage(error.response?.data?.message || 'Error loading users');
      }
    }
  );

  // Invite user mutation
  const inviteUserMutation = useMutation(
    customerAPI.inviteUser,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('company-users');
        setShowInviteModal(false);
        setInviteForm({ name: '', email: '' });
        setSuccessMessage('Invitation sent successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
      },
      onError: (error) => {
        console.error('Error inviting user:', error);
        setErrorMessage(error.response?.data?.message || 'Error inviting user');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  );

  // Update user mutation
  const updateUserMutation = useMutation(
    ({ id, userData }) => customerAPI.updateUser(id, userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('company-users');
        setShowEditModal(false);
        setSelectedUser(null);
        setSuccessMessage('User updated successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
      },
      onError: (error) => {
        console.error('Error updating user:', error);
        setErrorMessage(error.response?.data?.message || 'Error updating user');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    customerAPI.deleteUser,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('company-users');
        setSuccessMessage('User deleted successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
      },
      onError: (error) => {
        console.error('Error deleting user:', error);
        setErrorMessage(error.response?.data?.message || 'Error deleting user');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  );

  // Toggle user status mutation
  const toggleUserStatusMutation = useMutation(
    ({ userId, isActive }) => {
      return isActive ? customerAPI.deactivateUser(userId) : customerAPI.activateUser(userId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('company-users');
        setSuccessMessage('User status updated successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
      },
      onError: (error) => {
        console.error('Error updating user status:', error);
        setErrorMessage(error.response?.data?.message || 'Error updating user status');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  );

  const handleInviteFormChange = (e) => {
    const { name, value } = e.target;
    setInviteForm(prev => ({ ...prev, [name]: value }));
  };

  const handleInviteSubmit = (e) => {
    e.preventDefault();
    // Always set role to 'regular_user' for new invites
    const inviteData = {
      ...inviteForm,
      role: 'regular_user'
    };
    inviteUserMutation.mutate(inviteData);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      permissions: user.permissions || [],
      isActive: user.status === 'active'
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const userData = {
      name: editForm.name,
      email: editForm.email,
      role: editForm.role,
      permissions: editForm.permissions
    };
    if (editForm.permissions.length > 0 && editForm.role !== 'customer_admin') {
      userData.role = 'custom';
    }
    updateUserMutation.mutate(
      { id: selectedUser.id, userData },
      {
        onSuccess: async () => {
          const shouldBeActive = editForm.isActive;
          const isCurrentlyActive = selectedUser.status === 'active';

          if (shouldBeActive !== isCurrentlyActive) {
            await toggleUserStatusMutation.mutateAsync({
              userId: selectedUser.id,
              isActive: isCurrentlyActive
            });
          }
        }
      }
    );
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      'customer_admin': 'Admin',
      'regular_user': 'User',
      'custom': 'Custom'
    };
    return roleMap[role] || role;
  };

  const getStatusDisplay = (status) => {
    const statusConfig = {
      'active': { label: 'Active', color: 'bg-green-100 text-green-800', icon: FiUserCheck },
      'pending': { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: FiMail },
      'inactive': { label: 'Inactive', color: 'bg-red-100 text-red-800', icon: FiUserX }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  if (isLoading || authLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="py-6 px-4">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4">
          <SuccessAlert message={successMessage} />
        </div>
      )}
      {errorMessage && (
        <div className="mb-4">
          <ErrorAlert message={errorMessage} />
        </div>
      )}

      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your company users and invite new team members.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          {canInviteUsers() && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FiPlus className="-ml-1 mr-2 h-5 w-5" />
              Invite User
            </button>
          )}
        </div>
      </div>

      {/* Users table */}
      <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                User
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Role
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Status
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Last Login
              </th>
              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users?.data?.map((user) => {
              const statusConfig = getStatusDisplay(user.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <tr key={user.id}>
                  <td className="whitespace-nowrap px-4 py-4 sm:px-6">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <FiUser className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {user.name}
                          {user.is_primary && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                    {getRoleDisplay(user.role)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <div className="flex justify-end">
                      {!user.is_primary && user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit user"
                        >
                          <FiEdit className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {users?.data?.length === 0 && (
        <div className="text-center py-12">
          <FiUser className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by inviting a new user to your company.
          </p>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Invite New User
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Send an invitation to join your company
              </p>
            </div>
            <form onSubmit={handleInviteSubmit}>
              <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={inviteForm.name}
                      onChange={handleInviteFormChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={inviteForm.email}
                      onChange={handleInviteFormChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <div className="mt-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      <span className="text-sm text-gray-900">User</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      New users are assigned the "User" role by default. They will receive a password setup link and appear as pending until they activate their account.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={inviteUserMutation.isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {inviteUserMutation.isLoading ? 'Sending...' : 'Send Invitation'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Edit User: {selectedUser.name}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Update user information and permissions
              </p>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="edit-name"
                      required
                      value={editForm.name}
                      onChange={handleEditFormChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="edit-email"
                      required
                      value={editForm.email}
                      onChange={handleEditFormChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      name="role"
                      id="edit-role"
                      value={editForm.role}
                      onChange={handleEditFormChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="regular_user">User</option>
                      <option value="customer_admin">Admin</option>
                      <option value="custom">Custom</option>
                    </select>
                    <p className="mt-2 text-sm text-gray-500">
                      Role will automatically change to "Custom" when specific permissions are assigned.
                    </p>
                  </div>

                  <div className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-3">
                    <div>
                      <label htmlFor="edit-isActive" className="block text-sm font-medium text-gray-700">
                        User Status
                      </label>
                      <p className="text-sm text-gray-500">
                        {editForm.isActive ? 'This user can sign in and access the portal.' : 'This user is deactivated and cannot sign in.'}
                      </p>
                    </div>
                    <input
                      id="edit-isActive"
                      name="isActive"
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Custom Permissions Section - Basic implementation */}
                  {editForm.role === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Custom Permissions
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Invite Users</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Manage Inventory</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Create Requests</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">View Company Details</span>
                        </label>
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Select specific permissions for this user.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="border-t border-gray-200 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={updateUserMutation.isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {updateUserMutation.isLoading ? 'Updating...' : 'Update User'}
                </button>
                {!selectedUser.is_primary && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      handleDeleteUser(selectedUser.id);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    <FiTrash2 className="mr-2 h-4 w-4" />
                    Delete User
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 
